import * as React from "react"

import { CheckIcon, ChevronDownIcon } from "lucide-react"
import { useFormContext, type FieldPath } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface SelectOption {
  value: string | number
  label: string
}

interface FormSearchableSelectProps<T extends Record<string, unknown>> {
  name: FieldPath<T>
  options: SelectOption[]
  placeholder: string
  disabled?: boolean
}

export function FormSearchableSelect<T extends Record<string, unknown>>({
  name,
  options,
  placeholder,
  disabled = false,
}: FormSearchableSelectProps<T>) {
  const [open, setOpen] = React.useState(false)
  const form = useFormContext<T>()

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const selectedLabel =
          options.find((option) => option.value === field.value)?.label ?? ""

        return (
          <FormItem>
            <FormControl>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded="false"
                    disabled={disabled}
                    className={cn(
                      "w-full justify-between",
                      disabled && "opacity-50",
                    )}
                  >
                    {selectedLabel ? (
                      selectedLabel
                    ) : (
                      <span className="text-muted hover:text-muted inline-block">
                        {placeholder}
                      </span>
                    )}
                    <ChevronDownIcon className="size-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className={cn(
                    "w-[var(--radix-popover-trigger-width)] p-0",
                    "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border shadow-md",
                  )}
                >
                  <Command className="">
                    <CommandInput placeholder={`Search ${placeholder}...`} />
                    <CommandEmpty className="text-muted-foreground py-6 text-center text-sm">
                      No results found.
                    </CommandEmpty>
                    <CommandGroup>
                      {options.map((option) => (
                        <CommandItem
                          key={option.label}
                          value={option.label}
                          onSelect={(currentValue) => {
                            field.onChange(
                              currentValue === field.value
                                ? ""
                                : options.find(
                                    (option) => option.label === currentValue,
                                  )?.value,
                            )
                            setOpen(false)
                          }}
                          className={cn(
                            "focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pl-2 text-sm outline-none select-none",
                          )}
                        >
                          <span className="flex flex-1 items-center gap-2">
                            {option.label}
                          </span>
                          <CheckIcon
                            className={cn(
                              "h-4 w-4",
                              field.value === option.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </FormControl>
            <FormMessage className="form-error" />
          </FormItem>
        )
      }}
    />
  )
}
